import { useEffect, useState } from 'react';

import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';

import legacyTheme from '../../../theme';
import { tokens } from '../../../theme-next';

interface CertifySubmissionModalProps {
  open: boolean;
  onClose: () => void;
  /** Confirmation de la certification → déclenche la génération de la demande/facture. */
  onConfirm: () => void;
}

/**
 * Popup de certification, dernier verrou AVANT la génération de la facture
 * (étape `recap`). L'agence atteste sur l'honneur que les champs vérifiés sont
 * cohérents et complets. La case doit être cochée pour activer la confirmation.
 *
 * Affiché dans les deux chemins de soumission :
 *  - directement après l'étape de vérification (plaque/département inchangés) ;
 *  - après re-validation du tarif recalculé (plaque/département modifiés).
 */
export const CertifySubmissionModal = ({ open, onClose, onConfirm }: CertifySubmissionModalProps) => {
  const [checked, setChecked] = useState(false);

  // Réinitialise la case à chaque ouverture pour forcer un acte explicite.
  useEffect(() => {
    if (open) setChecked(false);
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: '10px', paddingBottom: '8px' }}>
        <VerifiedRoundedIcon sx={{ color: tokens.color.green, fontSize: 24 }} />
        <Typography component="span" sx={{ fontSize: '20px', fontWeight: 800, color: tokens.color.dark }}>
          Certification de la demande
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: '14.5px', lineHeight: 1.6, color: tokens.color.dark }}>
          Avant de générer la demande, vous attestez avoir relu les informations extraites des pièces et confirmez
          qu'elles sont <strong>exactes, cohérentes et complètes</strong>.
        </Typography>
        <Box
          sx={{
            marginTop: '16px',
            padding: '12px 14px',
            backgroundColor: tokens.color.surface,
            border: `1px solid ${tokens.color.border}`,
            borderRadius: `${tokens.radius.lg}px`,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                sx={{
                  paddingTop: '2px',
                  alignSelf: 'flex-start',
                  color: tokens.color.green,
                  '&.Mui-checked': { color: tokens.color.green },
                }}
              />
            }
            sx={{ alignItems: 'flex-start', marginRight: 0 }}
            label={
              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: tokens.color.dark, lineHeight: 1.5 }}>
                Je certifie sur l'honneur que les informations saisies sont exactes, cohérentes et complètes, et
                qu'elles correspondent aux pièces fournies.
              </Typography>
            }
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: '12px 24px 20px' }}>
        <ThemeProvider theme={legacyTheme}>
          <Button className="secondaryDealActionButton" onClick={onClose}>
            Annuler
          </Button>
          <Button
            className="dealActionButton"
            onClick={onConfirm}
            disabled={!checked}
            color="primary"
            variant="contained"
          >
            Confirmer et générer la demande →
          </Button>
        </ThemeProvider>
      </DialogActions>
    </Dialog>
  );
};

export default CertifySubmissionModal;
